import SFTypography from "../typography";
import { SFStack } from "../grid";
import PropTypes from "prop-types";
import getStyled from "../../hooks/getStyled";
import SFLink from "../link";
import { useTranslation } from "react-i18next";
import SFTooltipButtonIcon from "../tooltip/info";

const { styled } = getStyled();

const SFTypographyStyled = styled(SFTypography, {
  shouldForwardProp: false,
})(({ theme, preWrap, hideOverflow }) => ({
  width: "100%",
  ...(hideOverflow && {
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  whiteSpace: preWrap ? "normal" : "nowrap",
}));

const SFDetailField = ({
  label,
  value,
  variant = "default",
  spacing = 1,
  children,
  preWrap = false,
  hideOverflow = true,
  hideValueIfEmpty = false,
  helpText = null,
  helpTextPosition = "bottom-start",
  labelVariant = "rightPanelLabel",
  hasHtml = false,
  target,
}) => {
  const { t } = useTranslation();
  const getContent = () => {
    if (typeof value === "undefined" || value === null) {
      return "-";
    }
    switch (variant) {
    case "link":
      return <SFLink href={value} target={target}>{value}</SFLink>;
    case "boolean":
      return value ? t("generic.yes") : t("generic.no");
    case "text":
    default:
      return value;
    }
  };

  return (
    <SFStack alignItems="flex-start" spacing={spacing ?? 1}>
      <SFTypography
        variant={labelVariant}
        sx={{ display: "inline-flex", alignItems: "center" }}
      >
        {label}
        {helpText && (
          <SFTooltipButtonIcon tooltip={helpText} position={helpTextPosition} />
        )}
      </SFTypography>
      {!value && hideValueIfEmpty ? null : (
        <SFTypographyStyled
          preWrap={preWrap}
          variant="body1"
          hideOverflow={hideOverflow}
          hasHtml={hasHtml}
        >
          {getContent()}
        </SFTypographyStyled>
      )}
      {children}
    </SFStack>
  );
};

SFDetailField.propTypes = {
  //Label of the field
  label: PropTypes.string.isRequired,
  //Value of the field
  value: PropTypes.string,
  //Content variant
  variant: PropTypes.oneOf(["text", "link", "boolean"]),
  //Spacing between label, value and children
  spacing: PropTypes.number,
  //Content bellow the value field
  children: PropTypes.element,
  //wraps-text
  preWrap: PropTypes.bool,
  //tooltip message
  helpText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  //tooltip message position
  helpTextPosition: PropTypes.string,
  //the typography variant for the label
  labelVariant: PropTypes.string,
  //indicates if the content contains HTML
  hasHtml: PropTypes.bool,
  //target attribute for links (e.g., "_blank")
  target: PropTypes.string,
};

export default SFDetailField;
